import type { PropsWithChildren } from 'react';
import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import type { TBbox } from '@vlabs/monokit/shared/ui/canvas';

export interface TChecksResultContext {
  checkResult?: boolean;
  updateCheckResult?: (newResult: boolean) => void;
  bboxList?: TBbox[];
  setBboxList?: (bboxList: TBbox[]) => void;
}

const ChecksResultContext = createContext<TChecksResultContext>({
  checkResult: undefined,
  updateCheckResult: undefined,
  bboxList: undefined,
  setBboxList: undefined,
});

function CheckResultsProvider({ children }: PropsWithChildren): React.ReactNode {
  const [checkResult, setCheckResult] = useState<boolean>();
  const [bboxList, setBboxList] = useState<TBbox[]>();

  const updateCheckResult = useCallback((newResult: boolean): void => {
    if (checkResult === undefined) {
      setCheckResult(newResult);
      return;
    }
    if ((checkResult && newResult) !== checkResult) {
      setCheckResult(checkResult && newResult);
    }
  }, [checkResult]);

  const contextValue = useMemo<TChecksResultContext>(() => ({
    checkResult,
    updateCheckResult,
    bboxList,
    setBboxList,
  }), [checkResult, updateCheckResult, bboxList, setBboxList]);

  return (
    <ChecksResultContext.Provider value={contextValue}>
      {children}
    </ChecksResultContext.Provider>
  );
}

const useChecksResultContext = (): TChecksResultContext => {
  return useContext(ChecksResultContext);
};

export { ChecksResultContext, CheckResultsProvider, useChecksResultContext };
